/**
 * SharePoint site, list, and item tools via Microsoft Graph
 */

import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { GraphApi } from "../api/graph-api.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

// =============================================================================
// Tool Definitions
// =============================================================================

export const searchSharePointSitesTool = {
  name: "search_sharepoint_sites",
  description:
    "Search for SharePoint sites by name or keyword. Returns site IDs, names, and URLs. Use the site ID in subsequent SharePoint operations.",
  inputSchema: {
    type: "object" as const,
    properties: {
      query: {
        type: "string",
        description: "Search term to find sites (e.g., 'Marketing', 'HR Portal')",
      },
    },
    required: ["query"],
  },
};

export const getSharePointSiteTool = {
  name: "get_sharepoint_site",
  description:
    "Get a SharePoint site by its ID or by hostname and path. Returns site details including name, URL, and description.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The site ID (from search_sharepoint_sites). Cannot be used with hostname/sitePath.",
      },
      hostname: {
        type: "string",
        description: "The SharePoint hostname (e.g., 'contoso.sharepoint.com'). Use with sitePath.",
      },
      sitePath: {
        type: "string",
        description: "The site path (e.g., '/sites/marketing'). Use with hostname.",
      },
    },
  },
};

export const listSharePointListsTool = {
  name: "list_sharepoint_lists",
  description:
    "List all lists and libraries in a SharePoint site. Returns list IDs, names, and types. Use the list ID for item operations.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
    },
    required: ["siteId"],
  },
};

export const getSharePointListColumnsTool = {
  name: "get_sharepoint_list_columns",
  description:
    "Get column definitions for a SharePoint list. Shows column names, types, and whether they are required. Use this before creating or updating items.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      listId: {
        type: "string",
        description: "The list ID",
      },
    },
    required: ["siteId", "listId"],
  },
};

export const listSharePointItemsTool = {
  name: "list_sharepoint_items",
  description:
    "Get items from a SharePoint list with optional filtering and sorting. Returns item IDs, field values, and metadata.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      listId: {
        type: "string",
        description: "The list ID",
      },
      filter: {
        type: "string",
        description: "OData filter expression (e.g., \"fields/Status eq 'Active'\")",
      },
      orderBy: {
        type: "string",
        description: "OData order by (e.g., 'fields/Created desc')",
      },
      top: {
        type: "number",
        description: "Maximum number of items to return (default 50)",
        minimum: 1,
        maximum: 250,
      },
    },
    required: ["siteId", "listId"],
  },
};

export const createSharePointItemTool = {
  name: "create_sharepoint_item",
  description:
    "Create a new item in a SharePoint list. Use get_sharepoint_list_columns first to see available fields. Provide field values as key-value pairs.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      listId: {
        type: "string",
        description: "The list ID",
      },
      fields: {
        type: "object",
        description: "Field values for the new item (e.g., {\"Title\": \"New Task\", \"Status\": \"Active\"})",
        additionalProperties: true,
      },
    },
    required: ["siteId", "listId", "fields"],
  },
};

export const updateSharePointItemTool = {
  name: "update_sharepoint_item",
  description:
    "Update an existing SharePoint list item. Only include fields you want to change.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      listId: {
        type: "string",
        description: "The list ID",
      },
      itemId: {
        type: "string",
        description: "The item ID to update",
      },
      fields: {
        type: "object",
        description: "Field values to update (e.g., {\"Status\": \"Completed\"})",
        additionalProperties: true,
      },
    },
    required: ["siteId", "listId", "itemId", "fields"],
  },
};

export const deleteSharePointItemTool = {
  name: "delete_sharepoint_item",
  description:
    "Delete a SharePoint list item permanently. This action cannot be undone. You must set confirm=true to proceed.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      listId: {
        type: "string",
        description: "The list ID",
      },
      itemId: {
        type: "string",
        description: "The item ID to delete",
      },
      confirm: {
        type: "boolean",
        description: "Set to true to confirm deletion. Required to prevent accidental deletions.",
      },
    },
    required: ["siteId", "listId", "itemId"],
  },
};

// =============================================================================
// Input Schemas
// =============================================================================

export const searchSharePointSitesInputSchema = z.object({
  query: z.string().min(1),
});

export const getSharePointSiteInputSchema = z.object({
  siteId: z.string().optional(),
  hostname: z.string().optional(),
  sitePath: z.string().optional(),
});

export const listSharePointListsInputSchema = z.object({
  siteId: z.string().min(1),
});

export const getSharePointListColumnsInputSchema = z.object({
  siteId: z.string().min(1),
  listId: z.string().min(1),
});

export const listSharePointItemsInputSchema = z.object({
  siteId: z.string().min(1),
  listId: z.string().min(1),
  filter: z.string().optional(),
  orderBy: z.string().optional(),
  top: z.number().min(1).max(250).optional(),
});

export const createSharePointItemInputSchema = z.object({
  siteId: z.string().min(1),
  listId: z.string().min(1),
  fields: z.record(z.string(), z.unknown()),
});

export const updateSharePointItemInputSchema = z.object({
  siteId: z.string().min(1),
  listId: z.string().min(1),
  itemId: z.string().min(1),
  fields: z.record(z.string(), z.unknown()),
});

export const deleteSharePointItemInputSchema = z.object({
  siteId: z.string().min(1),
  listId: z.string().min(1),
  itemId: z.string().min(1),
  confirm: z.boolean().optional(),
});

// =============================================================================
// Tool Handlers
// =============================================================================

const GRAPH_NOT_AVAILABLE = "Graph API not configured. SharePoint operations require Microsoft Graph access.";

export async function handleSearchSharePointSites(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = searchSharePointSitesInputSchema.parse(input);
  logger.info({ query: parsed.query }, "Executing search_sharepoint_sites");

  const sites = await graphApi.searchSites(parsed.query);

  if (sites.length === 0) {
    return `No SharePoint sites found matching "${parsed.query}".`;
  }

  const lines = [`SharePoint Sites (${sites.length}):`];
  for (const site of sites) {
    lines.push(`  • ${site.displayName}`);
    lines.push(`    ID: ${site.id}`);
    lines.push(`    URL: ${site.webUrl}`);
    if (site.description) {
      lines.push(`    Description: ${site.description.slice(0, 100)}${site.description.length > 100 ? "..." : ""}`);
    }
  }

  return lines.join("\n");
}

export async function handleGetSharePointSite(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = getSharePointSiteInputSchema.parse(input);
  logger.info(parsed, "Executing get_sharepoint_site");

  let site;
  if (parsed.siteId) {
    site = await graphApi.getSite(parsed.siteId);
  } else if (parsed.hostname && parsed.sitePath) {
    site = await graphApi.getSiteByUrl(parsed.hostname, parsed.sitePath);
  } else {
    return "Provide either siteId, or both hostname and sitePath.";
  }

  const lines = [
    `Site: ${site.displayName}`,
    `ID: ${site.id}`,
    `URL: ${site.webUrl}`,
    `Name: ${site.name}`,
  ];
  if (site.description) lines.push(`Description: ${site.description}`);
  if (site.siteCollection?.hostname) lines.push(`Hostname: ${site.siteCollection.hostname}`);

  return lines.join("\n");
}

export async function handleListSharePointLists(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = listSharePointListsInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId }, "Executing list_sharepoint_lists");

  const lists = await graphApi.listSiteLists(parsed.siteId);

  // Filter out hidden lists
  const visible = lists.filter((l) => !l.list.hidden);

  if (visible.length === 0) {
    return "No visible lists found in this site.";
  }

  const lines = [`SharePoint Lists (${visible.length}):`];
  for (const list of visible) {
    lines.push(`  • ${list.displayName}`);
    lines.push(`    ID: ${list.id}`);
    lines.push(`    Template: ${list.list.template}`);
    lines.push(`    URL: ${list.webUrl}`);
    lines.push(`    Modified: ${new Date(list.lastModifiedDateTime).toLocaleString()}`);
  }

  return lines.join("\n");
}

export async function handleGetSharePointListColumns(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = getSharePointListColumnsInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId, listId: parsed.listId }, "Executing get_sharepoint_list_columns");

  const columns = await graphApi.getListColumns(parsed.siteId, parsed.listId);

  // Filter out internal/system columns for cleaner output
  const userColumns = columns.filter((c) => !c.readOnly || c.name === "Title");

  const lines = [`List Columns (${userColumns.length} user-facing, ${columns.length} total):`];
  for (const col of userColumns) {
    const required = col.required ? " *required*" : "";
    const indexed = col.indexed ? " [indexed]" : "";
    lines.push(`  • ${col.displayName} (${col.name}) - ${col.type || "unknown"}${required}${indexed}`);
    if (col.description) lines.push(`    ${col.description}`);
  }

  return lines.join("\n");
}

export async function handleListSharePointItems(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = listSharePointItemsInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId, listId: parsed.listId }, "Executing list_sharepoint_items");

  const items = await graphApi.getListItems(parsed.siteId, parsed.listId, {
    filter: parsed.filter,
    orderBy: parsed.orderBy,
    top: parsed.top,
  });

  if (items.length === 0) {
    return `No items found in list${parsed.filter ? ` matching filter: ${parsed.filter}` : ""}.`;
  }

  const lines = [`List Items (${items.length}):`];
  for (const item of items) {
    lines.push(`\n  Item ${item.id}:`);
    if (item.fields) {
      for (const [key, value] of Object.entries(item.fields)) {
        if (key.startsWith("@odata") || key === "id") continue;
        if (value === null || value === undefined) continue;
        lines.push(`    ${key}: ${displayValue(value)}`);
      }
    }
    lines.push(`    Created: ${new Date(item.createdDateTime).toLocaleString()}`);
    lines.push(`    Modified: ${new Date(item.lastModifiedDateTime).toLocaleString()}`);
  }

  return lines.join("\n");
}

export async function handleCreateSharePointItem(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = createSharePointItemInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId, listId: parsed.listId }, "Executing create_sharepoint_item");

  const item = await graphApi.createListItem(parsed.siteId, parsed.listId, parsed.fields);

  const lines = [`Created item in list:`];
  lines.push(`  ID: ${item.id}`);
  lines.push(`  URL: ${item.webUrl}`);
  if (item.fields) {
    for (const [key, value] of Object.entries(item.fields)) {
      if (key.startsWith("@odata") || key === "id") continue;
      if (value === null || value === undefined) continue;
      lines.push(`  ${key}: ${displayValue(value)}`);
    }
  }

  return lines.join("\n");
}

export async function handleUpdateSharePointItem(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = updateSharePointItemInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId, listId: parsed.listId, itemId: parsed.itemId }, "Executing update_sharepoint_item");

  const updated = await graphApi.updateListItem(parsed.siteId, parsed.listId, parsed.itemId, parsed.fields);

  const lines = [`Updated item ${parsed.itemId}:`];
  for (const [key, value] of Object.entries(updated)) {
    if (key.startsWith("@odata") || key === "id") continue;
    if (value === null || value === undefined) continue;
    lines.push(`  ${key}: ${displayValue(value)}`);
  }

  return lines.join("\n");
}

export async function handleDeleteSharePointItem(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = deleteSharePointItemInputSchema.parse(input);

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Deletion not confirmed. Set confirm=true to delete item ${parsed.itemId}. This action cannot be undone.`);
  }

  logger.info({ siteId: parsed.siteId, listId: parsed.listId, itemId: parsed.itemId }, "Executing delete_sharepoint_item");

  await graphApi.deleteListItem(parsed.siteId, parsed.listId, parsed.itemId);

  return `Deleted item ${parsed.itemId} from list ${parsed.listId}.`;
}
